import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colorForField } from './fieldBoxes';
import styles from './DocumentBoxViewer.module.css';

const FieldBoxDotContext = createContext(null);

/**
 * Shares the boxes currently drawn on the document with the form, so each field can show a dot in
 * its box color. `fields` is null while no boxes are on screen (no positions, regular preview,
 * another document, boxes switched off): then no dot is rendered.
 */
export function FieldBoxDotProvider({ fields, fieldOrder, fieldLabels, activeField, onFieldClick, children }) {
    const value = useMemo(
        () => (fields && fields.size > 0 ? { fields, fieldOrder, fieldLabels, activeField, onFieldClick } : null),
        [fields, fieldOrder, fieldLabels, activeField, onFieldClick]
    );
    return <FieldBoxDotContext.Provider value={value}>{children}</FieldBoxDotContext.Provider>;
}

FieldBoxDotProvider.propTypes = {
    fields: PropTypes.instanceOf(Set),
    fieldOrder: PropTypes.arrayOf(PropTypes.string),
    fieldLabels: PropTypes.object,
    activeField: PropTypes.string,
    onFieldClick: PropTypes.func,
    children: PropTypes.node,
};

/**
 * Colored circle for a form field whose value has a box on the document (same color as the box).
 * Renders nothing when the field has no box on screen.
 *
 * placement "label": inline, after the field's title text;
 * "corner": top corner of the field's [data-ocr-field] container (table cells, summary tiles
 * without a title). endOffset: px from the inline end of that container.
 */
export default function FieldBoxDot({ field, placement = 'label', endOffset = 10 }) {
    const ctx = useContext(FieldBoxDotContext);
    const { t, i18n } = useTranslation();
    if (!ctx || !ctx.fields.has(field)) return null;

    const isRtl = i18n?.dir?.() === 'rtl';
    const isCorner = placement === 'corner';
    const label = ctx.fieldLabels?.[field] || field;
    const className = [
        styles.fieldDot,
        isCorner ? styles.fieldDotCorner : styles.fieldDotLabel,
        ctx.activeField === field ? styles.fieldDotActive : '',
    ].join(' ');

    return (
        <Tooltip title={`${label} · ${t('ShowOnDocument')}`} placement="top" arrow disableInteractive>
            <button
                type="button"
                tabIndex={-1}
                className={className}
                data-field-dot={field}
                aria-label={`${label} · ${t('ShowOnDocument')}`}
                style={{
                    '--box-color': colorForField(field, ctx.fieldOrder),
                    ...(isCorner ? { [isRtl ? 'left' : 'right']: endOffset } : null),
                }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => ctx.onFieldClick?.(field)}
            />
        </Tooltip>
    );
}

FieldBoxDot.propTypes = {
    field: PropTypes.string.isRequired,
    placement: PropTypes.oneOf(['label', 'corner']),
    endOffset: PropTypes.number,
};
